import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Fade,
  Typography,
  useTheme,
} from '@mui/material';
import { SmartToy as SmartToyIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCoco } from 'context/CocoContext';
import CocoMessageInput from './CocoMessageInput';
import ChatLoading from './ChatLoading';

const USER_MESSAGE_MAX_WIDTH = '70%';
const AI_MESSAGE_MAX_WIDTH = '100%';
const SCROLL_THRESHOLD = 10;
const BORDER_RADIUS = 3;

export default function CocoChat() {
  const theme = useTheme();
  const { messages, messagesLoading, streamingContent, isStreaming } = useCoco();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);

  // Track scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom =
        Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) <
        SCROLL_THRESHOLD;
      setIsUserScrolledUp(!atBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll only when user hasn't scrolled up
  useEffect(() => {
    if (!isUserScrolledUp) {
      const container = containerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }
  }, [messages, streamingContent, isUserScrolledUp]);

  const isEmpty = messages.length === 0 && !isStreaming;

  const renderMessage = (
    msg: { role: string; content: string },
    index: number,
    isStreamingMsg?: boolean,
  ) => {
    const isUser = msg.role === 'user';
    const messageMaxWidth = isUser ? USER_MESSAGE_MAX_WIDTH : AI_MESSAGE_MAX_WIDTH;

    return (
      <Box
        key={index}
        display="flex"
        flexDirection="column"
        alignItems={isUser ? 'flex-end' : 'flex-start'}
        mb={3}
      >
        {/* Sender header */}
        <Box
          sx={{
            display: 'flex',
            mb: 0.5,
            alignItems: 'center',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          {!isUser && (
            <Box
              sx={{
                width: 24,
                height: 24,
                display: 'flex',
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${theme.palette.primary.light}20`,
              }}
            >
              <SmartToyIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />
            </Box>
          )}
          <Box sx={{ mx: 0.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {isUser ? 'You' : 'Coco'}
            </Typography>
          </Box>
          {isUser && <Avatar sx={{ width: 24, height: 24 }} />}
        </Box>

        {/* Message bubble */}
        {msg.content && (
          <Fade in timeout={400} style={{ transitionDelay: isStreamingMsg ? '0ms' : '400ms' }}>
            <Box
              sx={{
                mt: 1,
                p: 2,
                maxWidth: messageMaxWidth,
                overflow: 'auto',
                bgcolor: isUser
                  ? `${theme.palette.primary.light}15`
                  : theme.palette.background.default,
                color: theme.palette.text.primary,
                borderRadius: BORDER_RADIUS,
                border: `1px solid ${theme.palette.divider}`,
                textAlign: isUser ? 'right' : 'left',
                '& p, & h1, & h2, & h3, & h4, & h5, & h6': { m: 0 },
                '& p + p': { mt: 1 },
                '& ul, & ol': { m: 0, pl: 3, textAlign: 'left' },
                '& li': { mb: 0.5 },
                '& pre': {
                  bgcolor: '#1E1E1E',
                  color: '#D4D4D4',
                  p: 1.5,
                  borderRadius: 1,
                  overflowX: 'auto',
                  fontSize: '0.85rem',
                },
                '& code': { fontSize: '0.85rem' },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 0.5,
                    fontSize: '0.85rem',
                  },
                },
              }}
            >
              {isUser ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.palette.primary.main }}
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <Box sx={{ flexGrow: 1, display: 'contents' }}>
        <Box ref={containerRef} sx={{ p: 2, maxHeight: '100%', overflowY: 'auto' }}>
          {messagesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <ChatLoading />
            </Box>
          ) : isEmpty ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
              gap={1}
            >
              <SmartToyIcon sx={{ fontSize: 48, color: theme.palette.primary.light }} />
              <Typography color="text.secondary" variant="body1">
                Hi, I'm Coco. How can I help?
              </Typography>
            </Box>
          ) : (
            <>
              {messages.map((msg, i) => renderMessage(msg, i))}
              {isStreaming && streamingContent &&
                renderMessage({ role: 'assistant', content: streamingContent }, -1, true)
              }
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>
      </Box>
      {isStreaming && !streamingContent && <ChatLoading />}
      <CocoMessageInput />
    </Box>
  );
}
